import React, { useState, useRef } from 'react';
import { View, TouchableOpacity, Animated, Easing, ScrollView, Pressable } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { styles } from './DropdownCustom.style';
import { brand, neutrals } from 'src/theme/colors';
import TextLato from '../text/text-lato/TextLato';
import TextDynamic from '../text/text-dynamic/TextDynamic';
import { generateSelectedTestID, generateTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';

const DropdownCustom = ({
  label = '',
  options = [],
  selected,
  onSelect,
  isFont = false,
  testID,
}: {
  label: string;
  options: any[];
  selected: any;
  onSelect: (value: any) => void;
  isFont?: boolean;
  testID?: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const animation = useRef(new Animated.Value(0)).current;

  const toggleDropdown = () => {
    Animated.timing(animation, {
      toValue: expanded ? 0 : 1,
      duration: 250,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
    setExpanded(!expanded);
  };

  const rotateIcon = animation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const optionHeight = 40;
  const maxHeight = 180;
  const targetHeight = Math.min(options?.length * optionHeight, maxHeight);

  const dropdownHeight = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, targetHeight],
  });

  return (
    <View style={styles.container} testID={testID}>
      <TextLato style={styles.label}>{label}</TextLato>
      <View style={styles.dropdownContainer}>
        <TouchableOpacity onPress={toggleDropdown} activeOpacity={0.8}>
          <View style={styles.dropdown}>
            <TextDynamic
              fontFamily={isFont ? selected : ''}
              size={14}
              color={neutrals.neutralGrays.graphite}
              fontStyle="regular"
              testID={generateSelectedTestID(generateTestID(testID, TestIDs.DROPDOWN_OPTION), true)}
            >
              {selected}
            </TextDynamic>
            <Animated.View style={{ transform: [{ rotate: rotateIcon }] }}>
              <Ionicons name="chevron-down" size={20} color={brand.main.water} />
            </Animated.View>
          </View>
        </TouchableOpacity>

        <Animated.View style={[styles.dropdownList, { height: dropdownHeight }]}>
          <ScrollView nestedScrollEnabled>
            {options?.map((opt, index) => {
              const testId = generateTestID(testID, `${TestIDs.DROPDOWN_OPTION}-${index}`);
              return (
                <Pressable
                  testID={testId}
                  key={generateTestID(testID, TestIDs.DROPDOWN_OPTION, opt)}
                  onPress={() => {
                    onSelect(opt);
                    toggleDropdown();
                  }}
                  style={(state: any) => [styles.option, state?.hovered && styles.hover]}
                >
                  <TextDynamic
                    fontFamily={isFont ? opt : ''}
                    size={14}
                    color={selected != opt ? neutrals.neutralGrays.graphite : brand.main.water}
                    fontStyle="regular"
                    testID={generateTestID(testId, TestIDs.TEXT)}
                  >
                    {opt}
                  </TextDynamic>
                </Pressable>
              );
            })}
          </ScrollView>
        </Animated.View>
      </View>
    </View>
  );
};

export default DropdownCustom;
